import React, { useState, useRef, useCallback } from 'react';
import {
  FiScissors,
  FiYoutube,
  FiUpload,
  FiLink,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiDownload,
  FiClock,
  FiZap,
} from 'react-icons/fi';
import { videoService } from '../services/apiService';
import { useT } from '../utils/i18n';

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEP_IDS = ['fetch', 'analyse', 'extract'];

const StepIndicator = ({ currentStep, completedSteps, steps }) => (
  <div className="space-y-2">
    {steps.map((label, idx) => {
      const id       = STEP_IDS[idx];
      const isDone   = completedSteps.has(id);
      const isActive = id === currentStep && !isDone;
      return (
        <div key={id} className="flex items-center gap-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
            isDone    ? 'bg-green-500'
            : isActive ? 'bg-primary-500'
            : 'bg-gray-200'
          }`}>
            {isDone ? (
              <FiCheck size={12} className="text-white" />
            ) : isActive ? (
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="text-[10px] font-bold text-gray-400">{idx + 1}</span>
            )}
          </div>
          <span className={`text-sm ${isDone ? 'text-green-700 font-medium' : isActive ? 'text-primary-700 font-medium' : 'text-gray-400'}`}>
            {label}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── Clip card ────────────────────────────────────────────────────────────────

const ClipCard = ({ clip, downloadUrl, t }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
    {/* Header */}
    <div className="bg-gradient-to-r from-primary-50 to-purple-50 px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-bold text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
            {t('videoToVideo.clip')} {clip.index}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <FiClock size={11} />
            {clip.duration}{t('videoToVideo.seconds')}
          </span>
          <span className="text-xs text-gray-400">{(clip.sizeMB).toFixed(1)} MB</span>
        </div>
        <h3 className="text-sm font-semibold text-gray-800 leading-tight truncate">{clip.title}</h3>
      </div>
      <a
        href={downloadUrl}
        download={clip.filename}
        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-colors"
      >
        <FiDownload size={13} />
        {t('videoToVideo.download')}
      </a>
    </div>

    {/* Body */}
    <div className="px-4 py-3 space-y-2">
      {clip.hook && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
            {t('videoToVideo.hook')}
          </p>
          <p className="text-sm text-gray-700 leading-snug">{clip.hook}</p>
        </div>
      )}
      {clip.platform && (
        <div className="flex items-center gap-1.5">
          <FiZap size={12} className="text-yellow-500 flex-shrink-0" />
          <span className="text-xs text-gray-500">{t('videoToVideo.platform')}: </span>
          <span className="text-xs font-medium text-gray-700">{clip.platform}</span>
        </div>
      )}
      <div className="text-[11px] text-gray-400">
        {clip.start.toFixed(1)}s – {clip.end.toFixed(1)}s
      </div>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const VideoToVideo = () => {
  const t = useT();

  const [inputMode, setInputMode]         = useState('url');   // 'url' | 'upload'
  const [youtubeUrl, setYoutubeUrl]       = useState('');
  const [file, setFile]                   = useState(null);
  const [isDragging, setDragging]         = useState(false);

  const [currentStep, setCurrentStep]       = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isRunning, setIsRunning]           = useState(false);
  const [error, setError]                   = useState('');

  const [jobId, setJobId]   = useState(null);
  const [clips, setClips]   = useState([]);

  const fileInputRef = useRef(null);

  const steps = [
    t('videoToVideo.step1'),
    t('videoToVideo.step2'),
    t('videoToVideo.step3'),
  ];

  const markDone = useCallback((id) => {
    setCompletedSteps(prev => { const n = new Set(prev); n.add(id); return n; });
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setError('');
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, []);

  const handleRun = async () => {
    setError('');

    if (inputMode === 'url' && !youtubeUrl.trim()) {
      setError(t('videoToVideo.pasteUrl'));
      return;
    }
    if (inputMode === 'upload' && !file) {
      setError(t('videoToVideo.selectFile'));
      return;
    }

    setIsRunning(true);
    setCurrentStep('fetch');
    setCompletedSteps(new Set());
    setClips([]);
    setJobId(null);

    try {
      // Step 1 & 2 & 3 all happen server-side in one call; we animate steps
      // by advancing the indicator while the request is in flight.
      const fetchTimer    = setTimeout(() => { markDone('fetch');   setCurrentStep('analyse'); }, 8000);
      const analyseTimer  = setTimeout(() => { markDone('analyse'); setCurrentStep('extract'); }, 40000);

      const result = await videoService.extractClips(
        inputMode === 'url' ? youtubeUrl.trim() : null,
        inputMode === 'upload' ? file : null,
        (e) => { /* upload progress, not used for animation here */ }
      );

      clearTimeout(fetchTimer);
      clearTimeout(analyseTimer);

      markDone('fetch');
      markDone('analyse');
      markDone('extract');
      setCurrentStep(null);

      setJobId(result.jobId);
      setClips(result.clips || []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Something went wrong.';
      setError(msg);
      setCurrentStep(null);
    } finally {
      setIsRunning(false);
    }
  };

  const hasResults = clips.length > 0;

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-2 mb-0.5">
          <FiScissors size={20} className="text-primary-600" />
          <h1 className="text-xl font-bold text-gray-900">{t('videoToVideo.title')}</h1>
        </div>
        <p className="text-sm text-gray-500">{t('videoToVideo.desc')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        {/* ── Left panel ──────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {/* Input mode tabs */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex border-b border-gray-100">
              {[
                { id: 'url',    icon: FiLink,   label: t('videoToVideo.youtubeUrl') },
                { id: 'upload', icon: FiUpload, label: t('videoToVideo.uploadVideo') },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => !isRunning && setInputMode(id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                    inputMode === id
                      ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>

            <div className="p-4">
              {inputMode === 'url' ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 focus-within:border-primary-400 focus-within:ring-1 focus-within:ring-primary-200">
                    <FiYoutube size={16} className="text-red-500 flex-shrink-0" />
                    <input
                      type="url"
                      value={youtubeUrl}
                      onChange={e => setYoutubeUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      disabled={isRunning}
                      className="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-gray-400"
                    />
                    {youtubeUrl && (
                      <button onClick={() => setYoutubeUrl('')} className="text-gray-400 hover:text-gray-600">
                        <FiX size={14} />
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400">{t('videoToVideo.publicOnly')}</p>
                </div>
              ) : (
                <div
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => !isRunning && fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-primary-400 bg-primary-50'
                      : file
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp4,.mov,.avi,.mkv,.webm,video/*"
                    className="hidden"
                    onChange={e => handleFile(e.target.files[0])}
                    disabled={isRunning}
                  />
                  {file ? (
                    <div className="space-y-1">
                      <FiCheck size={24} className="text-green-500 mx-auto" />
                      <p className="text-sm font-medium text-gray-800 truncate max-w-[200px] mx-auto">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                      <button
                        onClick={e => { e.stopPropagation(); setFile(null); }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        {t('videoToVideo.remove')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <FiUpload size={24} className="text-gray-300 mx-auto" />
                      <p className="text-sm text-gray-500">
                        {isDragging ? t('videoToVideo.dropping') : t('videoToVideo.drop')}
                      </p>
                      <p className="text-[11px] text-gray-400">{t('videoToVideo.fileTypes')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Run button */}
          <button
            onClick={handleRun}
            disabled={isRunning}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <FiScissors size={16} />
            {isRunning ? t('videoToVideo.processing') : t('videoToVideo.generate')}
          </button>

          {/* Steps */}
          {currentStep && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <StepIndicator
                currentStep={currentStep}
                completedSteps={completedSteps}
                steps={steps}
              />
            </div>
          )}

          {/* Completed steps (after done) */}
          {!currentStep && completedSteps.size > 0 && hasResults && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <StepIndicator
                currentStep={null}
                completedSteps={completedSteps}
                steps={steps}
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
              <FiAlertCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 leading-snug">{error}</p>
            </div>
          )}
        </div>

        {/* ── Right panel: clips grid ──────────────────────────────────────── */}
        <div>
          {hasResults ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700">
                  {clips.length} viral clip{clips.length !== 1 ? 's' : ''} extracted
                </h2>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {clips.map(clip => (
                  <ClipCard
                    key={clip.index}
                    clip={clip}
                    downloadUrl={videoService.getClipDownloadUrl(jobId, clip.filename)}
                    t={t}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-8">
              <FiScissors size={36} className="text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-500">{t('videoToVideo.emptyTitle')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('videoToVideo.emptyDesc')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
